import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { ACCEPT_REASONS } from '../labels.ts';
import { useFocusTrap } from './useFocusTrap.ts';
import type { HygieneFinding, HygieneMember } from '../../../request/hygiene/hygieneRequests.ts';

/** Centered dialog shell (same backdrop and card as ConfirmDialogModal), with a form body. */
export function DialogShell({
  title,
  subtitle,
  onClose,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer: React.ReactNode;
}) {
  const titleId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  useFocusTrap(cardRef);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <div className="absolute inset-0" aria-hidden="true" onClick={onClose} />
      <div ref={cardRef} className="relative z-10 w-full max-w-md overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-base font-semibold text-gray-900">{title}</h2>
            {subtitle ? <p className="mt-0.5 truncate text-sm text-gray-500" title={subtitle}>{subtitle}</p> : null}
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4 px-5 py-4">{children}</div>
        <div className="flex flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/80 px-5 py-3 sm:flex-row sm:justify-end">{footer}</div>
      </div>
    </div>,
    document.body
  );
}

const secondaryBtn = 'rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50';
const primaryBtn = 'rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50';
const fieldClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

export function AssignDialog({
  finding,
  members,
  busy,
  onSubmit,
  onClose,
}: {
  finding: HygieneFinding;
  members: HygieneMember[];
  busy: boolean;
  onSubmit: (ownerUserId: number, note: string) => void;
  onClose: () => void;
}) {
  const [owner, setOwner] = useState<string>(finding.owner_user_id ? String(finding.owner_user_id) : '');
  const [note, setNote] = useState('');
  const ownerId = useId();
  const noteId = useId();
  return (
    <DialogShell
      title="Assign"
      subtitle={finding.record_label}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={secondaryBtn} onClick={onClose}>Cancel</button>
          <button type="button" className={primaryBtn} disabled={!owner || busy} onClick={() => onSubmit(Number(owner), note)}>
            {busy ? 'Saving…' : 'Assign'}
          </button>
        </>
      }
    >
      <div>
        <label htmlFor={ownerId} className="mb-1 block text-sm font-medium text-gray-700">Who should fix this?</label>
        <select data-autofocus id={ownerId} className={fieldClass} value={owner} onChange={(e) => setOwner(e.target.value)}>
          <option value="">Choose a person</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>
        {finding.owner_label && !finding.owner_user_id ? (
          <p className="mt-1 text-xs text-gray-500">The record names “{finding.owner_label}” as owner.</p>
        ) : null}
      </div>
      <div>
        <label htmlFor={noteId} className="mb-1 block text-sm font-medium text-gray-700">Note (optional)</label>
        <textarea id={noteId} rows={2} className={fieldClass} value={note} maxLength={4000} onChange={(e) => setNote(e.target.value)} />
      </div>
    </DialogShell>
  );
}

export function AcceptDialog({
  finding,
  busy,
  onSubmit,
  onClose,
}: {
  finding: HygieneFinding;
  busy: boolean;
  onSubmit: (reason: string, note: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const high = finding.severity === 'high';
  const noteRequired = high || reason === 'other';
  const valid = Boolean(reason) && (!noteRequired || note.trim().length > 0);
  const reasonId = useId();
  const noteId = useId();
  return (
    <DialogShell
      title="Accept this item"
      subtitle={finding.record_label}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={secondaryBtn} onClick={onClose}>Cancel</button>
          <button type="button" className={primaryBtn} disabled={!valid || busy} onClick={() => onSubmit(reason, note)}>
            {busy ? 'Saving…' : high ? 'Submit for approval' : 'Accept'}
          </button>
        </>
      }
    >
      <div>
        <label htmlFor={reasonId} className="mb-1 block text-sm font-medium text-gray-700">Reason</label>
        <select data-autofocus id={reasonId} className={fieldClass} value={reason} onChange={(e) => setReason(e.target.value)}>
          <option value="">Choose a reason</option>
          {ACCEPT_REASONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor={noteId} className="mb-1 block text-sm font-medium text-gray-700">
          Note {noteRequired ? '(required)' : '(optional)'}
        </label>
        <textarea id={noteId} rows={3} className={fieldClass} value={note} maxLength={4000} placeholder="Why this does not need fixing this year" onChange={(e) => setNote(e.target.value)} />
      </div>
      {high ? (
        <p className="border-l-4 border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          High items need an org admin’s approval, and you cannot approve your own request. The item stays open until it is approved.
        </p>
      ) : (
        <p className="text-xs text-gray-500">If the record changes later, the acceptance expires and the item reopens.</p>
      )}
    </DialogShell>
  );
}

/** One-note dialog for reject (approval), reopen and dismiss (AI). */
export function NoteDialog({
  title,
  subtitle,
  confirmLabel,
  noteRequired,
  busy,
  onSubmit,
  onClose,
}: {
  title: string;
  subtitle: string;
  confirmLabel: string;
  noteRequired: boolean;
  busy: boolean;
  onSubmit: (note: string) => void;
  onClose: () => void;
}) {
  const [note, setNote] = useState('');
  const noteId = useId();
  return (
    <DialogShell
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={secondaryBtn} onClick={onClose}>Cancel</button>
          <button type="button" className={primaryBtn} disabled={(noteRequired && !note.trim()) || busy} onClick={() => onSubmit(note)}>
            {busy ? 'Saving…' : confirmLabel}
          </button>
        </>
      }
    >
      <div>
        <label htmlFor={noteId} className="mb-1 block text-sm font-medium text-gray-700">Note {noteRequired ? '(required)' : '(optional)'}</label>
        <textarea data-autofocus id={noteId} rows={3} className={fieldClass} value={note} maxLength={4000} onChange={(e) => setNote(e.target.value)} />
      </div>
    </DialogShell>
  );
}
